/**
 * Ошибка переноса файла в кабинет — словами (v4 D3). Новый auto-ads пишет
 * текст по-русски; старый (и сеть стенда) отдавал сырой английский текст Node
 * («Client network socket disconnected before secure TLS connection…»). Такой
 * текст человеку не показывается: вместо него — понятная фраза, а сам текст —
 * в `detail` (во всплывашку, для разбора).
 */
const CYRILLIC = /[А-Яа-яЁё]/;
/** Технический английский текст: три и больше латинских слова подряд. */
const TECH_ENGLISH = /[A-Za-z]{2,}(?:[\s,'-]+[A-Za-z]{2,}){2,}/;
/** Сетевой сбой Node (TLS, сокет, таймаут, DNS). */
const NETWORK = /socket|TLS|ECONN|ETIMEDOUT|EAI_AGAIN|ENOTFOUND|network|fetch failed/i;
/** Коды ошибок переноса файла (auto-ads). */
const MEDIA_ERROR_TEXT = {
    'media-unavailable': 'Не удалось перенести файл в кабинет',
    'media-transfer-failed': 'Не удалось перенести файл в кабинет',
    'media-download-failed': 'Не удалось скачать файл из хранилища',
    'media-too-large': 'Файл слишком большой для Meta',
    'media-processing-failed': 'Meta не смогла обработать файл',
    'upload-network': 'Сбой сети при переносе файла в кабинет',
    'upload-timeout': 'Перенос файла в кабинет не уложился по времени',
    'meta-unavailable': 'Meta не ответила при переносе файла',
    'meta-rate-limited': 'Meta ограничила запросы при переносе файла',
};
const GENERIC = 'Не удалось перенести файл в кабинет';
/** Код — про перенос файла (`media-*`). */
export function isMediaErrorCode(code) {
    return code != null && (code.startsWith('media') || Object.hasOwn(MEDIA_ERROR_TEXT, code));
}
/**
 * Текст ошибки переноса: русский текст auto-ads — как есть; иначе фраза по
 * коду (или общая) + «повторите», а исходный текст — в `detail`.
 */
export function mediaErrorText(err) {
    const msg = err.message?.trim() ?? '';
    if (msg !== '' && CYRILLIC.test(msg) && !TECH_ENGLISH.test(msg)) {
        return { text: msg, detail: err.detail ?? null };
    }
    // «Перенос файла в кабинет не удался: Client network socket…» — русская
    // часть до двоеточия годится, английский хвост уходит в подробности
    const head = msg.split(/:\s/)[0]?.trim() ?? '';
    const base = head !== '' && CYRILLIC.test(head) && !TECH_ENGLISH.test(head)
        ? head
        : (MEDIA_ERROR_TEXT[err.code] ?? GENERIC);
    const why = NETWORK.test(msg) && !/сет/i.test(base)
        ? ' (сбой сети между сервисом рекламы и хранилищем файлов)'
        : '';
    return {
        text: `${base}${why} — повторите перенос`,
        detail: msg !== '' ? `${err.code}: ${msg}` : err.code,
    };
}
/** Код ошибки Meta (число, как в `error.code` Graph API). */
const META_NUMERIC_CODE = /^\d+$/;
/** Коды Meta (Graph API) → русская фраза для человека. */
const META_CODE_TEXT = {
    '100': 'Meta отклонила параметр или объект недоступен',
    '190': 'Доступ к Meta истёк — кабинет нужно подключить заново',
    '10': 'У подключения к Meta не хватает прав',
    '200': 'У подключения к Meta не хватает прав',
    '294': 'У подключения к Meta не хватает прав',
    '368': 'Meta временно ограничила действия в кабинете',
    '1': 'Meta временно не отвечает — повторите позже',
    '2': 'Meta временно не отвечает — повторите позже',
    '4': 'Meta ограничила число запросов — повторите позже',
    '17': 'Meta ограничила число запросов — повторите позже',
    '32': 'Meta ограничила число запросов — повторите позже',
    '613': 'Meta ограничила число запросов — повторите позже',
};
/**
 * Ошибка Meta словами (приёмка E-2, P2-10): русский текст Meta
 * (`error_user_msg` в нашей подаче) показываем как есть; английский
 * технический («Invalid parameter») человеку не показываем — вместо него фраза
 * по коду Meta или общая. Оригинал и код уходят в `detail` («Для поддержки»).
 */
export function metaErrorText(err) {
    const msg = err.message?.trim() ?? '';
    const isMeta = META_NUMERIC_CODE.test(err.code);
    const original = msg !== '' ? `${err.code}: ${msg}` : err.code;
    // код (`job-error` и т. п.) и оригинал — всегда в detail («Для поддержки»)
    const detail = err.detail != null && err.detail !== '' ? `${err.code}: ${err.detail}` : original;
    if (msg !== '' && CYRILLIC.test(msg))
        return { text: msg, detail };
    const text = isMeta ? (META_CODE_TEXT[err.code] ?? 'Meta отклонила запрос') : 'Шаг не выполнен';
    return { text, detail };
}
/**
 * Текст ошибки запуска в строке пачки: ошибка переноса файла — словами
 * (`mediaErrorText`), остальное — русский текст Meta как есть или фраза по коду
 * (`metaErrorText`).
 */
export function launchErrorText(err) {
    if (isMediaErrorCode(err.code) || /^Перенос файла/.test(err.message ?? '')) {
        return mediaErrorText(err);
    }
    return metaErrorText(err);
}
//# sourceMappingURL=media-errors.js.map